import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Activity, CheckCircle, HelpCircle, ArrowRight, Shield, Sun, Droplet } from 'react-feather';

const SkinCareAdvice = () => {
  const handleWhatsAppConsultation = () => {
    const message = "Hello Maxma Pharmacy, I would like to consult an expert about my skincare routine and concerns.";
    const whatsappUrl = `https://wa.me/254101757541?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1600428853876-fb5a850b444f?q=80&w=987&auto=format&fit=crop"
            alt="Skin Care Advice"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-orange-900/90 to-orange-600/40" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl text-white"
          >
            <div className="inline-flex items-center space-x-2 bg-orange-500/20 backdrop-blur-md border border-orange-400/30 rounded-full px-4 py-2 mb-6">
              <Droplet className="h-4 w-4 text-orange-200" />
              <span className="text-sm font-semibold tracking-wide uppercase">Radiant Health</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              Skin Care <br />
              <span className="text-orange-200">Advice</span>
            </h1>
            <p className="text-xl text-orange-50 mb-8 leading-relaxed">
              Personalized recommendations and expert guidance tailored to your unique skin type and concerns.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 -mt-20 relative z-20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            
            {/* Left Column: Info & Approach */}
            <div className="lg:col-span-8">
              <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="space-y-12"
              >
                {/* Intro Card */}
                <motion.div variants={itemVariants} className="bg-white p-8 md:p-12 rounded-3xl shadow-xl border border-gray-100">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                    <Heart className="mr-4 text-orange-500" />
                    Expert Skin Assessments
                  </h2>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">
                    Your skin is your body’s largest organ, and taking care of it is essential for overall health and confidence. 
                    Our experts provide personalized routines to help you achieve radiant, healthy skin.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      "Personalized skin type assessments",
                      "Customized skincare routines",
                      "Acne, dryness & aging guidance",
                      "Sun protection & lifestyle advice"
                    ].map((offer, index) => (
                      <div key={index} className="flex items-start p-4 bg-orange-50 rounded-2xl border border-orange-100">
                        <CheckCircle className="h-6 w-6 text-orange-500 mr-3 flex-shrink-0" />
                        <span className="font-semibold text-gray-800">{offer}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Our Approach */}
                <motion.div variants={itemVariants} className="bg-gradient-to-br from-orange-600 to-orange-700 p-8 md:p-12 rounded-3xl shadow-xl text-white relative overflow-hidden">
                  <div className="absolute right-0 bottom-0 opacity-10">
                    <Sun size={200} />
                  </div>
                  <div className="relative z-10">
                    <h2 className="text-3xl font-bold mb-6">Our Approach</h2>
                    <p className="text-xl text-orange-50 leading-relaxed mb-8">
                      We work with you to create a routine that fits your lifestyle. Whether you need help selecting products 
                      or managing a specific condition, we’re here to support your journey.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Shield className="h-5 w-5 mr-2" />
                        <span>Professional Support</span>
                      </div>
                      <div className="flex items-center bg-white/10 backdrop-blur-md rounded-full px-5 py-2">
                        <Droplet className="h-5 w-5 mr-2" />
                        <span>Tailored Routines</span>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* FAQ Section */}
                <motion.div variants={itemVariants} className="space-y-6">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                    <HelpCircle className="mr-4 text-orange-500" />
                    Common Questions
                  </h2>
                  <div className="grid gap-6">
                    {[
                      {
                        q: "How often should I update my skincare routine?",
                        a: "It’s recommended to update your routine as your skin changes, which can be seasonal or based on your skin’s response to products."
                      },
                      {
                        q: "What should I avoid in skincare products?",
                        a: "Avoid products with harsh chemicals, parabens, sulfates, and artificial fragrances if you have sensitive skin. Always patch-test new products."
                      }
                    ].map((faq, index) => (
                      <div key={index} className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                        <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-600 transition-colors">Q: {faq.q}</h3>
                        <p className="text-gray-600 leading-relaxed">A: {faq.a}</p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            </div>

            {/* Right Column: CTA / Sticky Info */}
            <div className="lg:col-span-4">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="sticky top-24"
              >
                <div className="bg-gray-900 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/20 blur-3xl -mr-16 -mt-16 rounded-full" />
                  <h3 className="text-2xl font-bold mb-4 relative z-10">Get your glow back</h3>
                  <p className="text-gray-400 mb-8 relative z-10">
                    Book a consultation with our experts today and start your journey to better skin health.
                  </p>
                  <button 
                    onClick={handleWhatsAppConsultation}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 rounded-2xl transition-all duration-300 flex items-center justify-center group"
                  >
                    Consult an Expert
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                  
                  <div className="mt-8 pt-8 border-t border-gray-800 space-y-4">
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Droplet className="h-5 w-5 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Hydration</p>
                        <p className="font-semibold">Healthy Skin Focus</p>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-300">
                      <div className="w-10 h-10 bg-gray-800 rounded-xl flex items-center justify-center mr-4">
                        <Shield className="h-5 w-5 text-orange-400" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Reliable</p>
                        <p className="font-semibold">Expert Recommendations</p>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default SkinCareAdvice;
